import React, { useState } from 'react';
import { ViewerRelation } from 'util/constants';
import { Blend } from 'lucide-react';
import { Progress } from 'ui/ProgressBar';
import DetailPanel from 'ui/DetailPanel';
import AlignmentDataCalculator, { AlignmentCalculation } from './AlignmentDataCalculator';
import AlignmentRadarChart from './AlignmentRadarChart';

interface AlignmentSummaryDisplayProps {
  targetUsers: any[]; // Array of users (for teams, this would be team members)
  currentUser: any | null;
  targetLabel: string; // Username or teamname
  currentUserViewerRelation: ViewerRelation;
  onStatementSelect: (statementId: number) => void;
  isTeam?: boolean;
  statementFilterIds?: number[] | null;
}

const AlignmentSummaryDisplay: React.FC<AlignmentSummaryDisplayProps> = ({
  targetUsers,
  currentUser,
  targetLabel,
  currentUserViewerRelation,
  onStatementSelect,
  isTeam = false,
  statementFilterIds = null,
}) => {
  const [alignmentData, setAlignmentData] = useState<AlignmentCalculation | null>(null);

  const handleDataCalculated = (data: AlignmentCalculation) => {
    setAlignmentData(data);
  };

  if (!alignmentData) {
    return (
      <div className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal p-1 rounded-lg">
        <AlignmentDataCalculator
          targetUsers={targetUsers}
          currentUser={currentUser}
          onDataCalculated={handleDataCalculated}
          isTeam={isTeam}
          statementFilterIds={statementFilterIds}
        />
      </div>
    );
  }

  const {
    similariteaPercentage,
    pureAlignmentPercentage,
    dimensionData,
    similarTopics,
    differentTopics,
    mutuallyImportantTopics,
    collaborationStatements,
    engagementStatements
  } = alignmentData;

  // Calculate importance percentage
  const importancePercentage = Math.round(
    mutuallyImportantTopics / Math.max(similarTopics + differentTopics, 1) * 100
  );

  // Legend component
  const renderLegend = () => (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex items-center">
        <div className="w-3 h-3 rounded-sm mr-1" style={{ backgroundColor: "rgba(99, 102, 241, 0.9)" }}></div>
        <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {isTeam ? targetLabel : "Them"}
        </span>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 rounded-sm mr-1" style={{ backgroundColor: "rgba(0, 82, 134, 0.9)" }}></div>
        <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">You</span>
      </div>
    </div>
  );

  return (
    <div className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal p-1 rounded-lg space-y-2">
      {/* Hidden data calculator to keep updating */}
      <div style={{ display: 'none' }}>
        <AlignmentDataCalculator
          targetUsers={targetUsers}
          currentUser={currentUser}
          onDataCalculated={handleDataCalculated}
          isTeam={isTeam}
          statementFilterIds={statementFilterIds}
        />
      </div>

      {/* Similaritea header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex font-semibold items-center flex-row gap-1 text-sm text-left text-eoe-text dark:text-eoe-dark-text w-fit">
          Similaritea 
          <span className="bg-eoe-icon dark:bg-eoe-dark-icon-hover flex flex-row items-center px-1.5 py-0 rounded-full text-eoe-text-sub/85 dark:text-white">
            {similariteaPercentage}
            <Blend className="w-4 h-4 ml-0.5 text-green-600" />
          </span>
        </div>
      </div>
      
      {/* Summary statement */}
      {(similarTopics > 0 || differentTopics > 0 || mutuallyImportantTopics > 0) && (
        <div className="text-xs text-left text-eoe-text dark:text-eoe-dark-text px-0">
          You {isTeam ? `and ${targetLabel}` : "both"} <span className="underline">agree on {similarTopics}</span> {similarTopics > 1 ? "claims" : "claim"} 
          {differentTopics > 0 && `, but `} 
          {differentTopics > 0 && <span className="underline">differ on {differentTopics}</span>} 
          {mutuallyImportantTopics > 0 && `, and find `} 
          {mutuallyImportantTopics > 0 && <span className="underline">{mutuallyImportantTopics} mutually important</span>}.
        </div>
      )}
      
      {/* Legend */}
      {renderLegend()}
      
      {/* Responsive layout container for radar and bars */}
      <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-center md:items-start">
        {/* Radar chart */}
        <div className="w-full flex justify-center">
          <AlignmentRadarChart
            dimensionData={dimensionData}
            targetLabel={isTeam ? targetLabel : "Them"}
            currentLabel="You"
            size={320}
          />
        </div>
        
        {/* Percentage bars */}
        <div className="w-full flex flex-col gap-3 justify-center">
          {/* Alignment percentage */}
          <div className="relative">
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-1">Alignment</p>
            <Progress value={pureAlignmentPercentage} className="bg-eoe-blue/20">
              <div className="absolute inset-0 flex items-center justify-center text-xs text-white">
                {pureAlignmentPercentage}%
              </div>
            </Progress>
          </div>
          
          {/* Importance percentage */}
          <div className="relative">
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-1">Importance</p>
            <Progress value={importancePercentage} className="bg-eoe-blue/20">
              <div className="absolute inset-0 flex items-center justify-center text-xs text-white">
                {importancePercentage}%
              </div>
            </Progress>
          </div>
        </div>
      </div>
      
      {/* Connect section */}
      {(engagementStatements.length > 0 || collaborationStatements.length > 0) && (
        <DetailPanel 
          title="Connect"
          className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-modal-hover"
          defaultOpen={true}
          headingContent={collaborationStatements.length > 0 && (
            <p className="text-xs text-left text-eoe-text dark:text-eoe-dark-text">
              Collaborate with {targetLabel} on {collaborationStatements.length > 1 ? "these" : "this"} {collaborationStatements.length} topic{collaborationStatements.length !== 1 ? 's' : ''} you are {isTeam ? "all" : "both"} passionate about:
            </p>
          )}
        >
          {collaborationStatements.length > 0 && (
            <div className="mb-3">
              <div className="space-y-1">
                {collaborationStatements.map((item) => (
                  <button
                    key={`collab-${item.id}`}
                    onClick={() => onStatementSelect(item.id)}
                    className="w-full px-2 py-1 text-left text-xs border rounded-md border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                  >
                    <span className="border rounded-full px-1 mr-1 border-eoe-text dark:border-eoe-dark-text">{item.id}</span> {item.statement}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {engagementStatements.length > 0 && (
            <div>
              <p className="text-left text-xs text-eoe-text dark:text-eoe-dark-text mb-1">
                Engage {targetLabel} on {engagementStatements.length > 1 ? "these" : "this"} {engagementStatements.length} topic{engagementStatements.length !== 1 ? 's' : ''} you don't agree on to build mutual understanding:
              </p>
              <div className="space-y-1">
                {engagementStatements.map((item) => (
                  <button
                    key={`engage-${item.id}`}
                    onClick={() => onStatementSelect(item.id)}
                    className="w-full px-2 py-1 text-left text-xs border rounded-md border-eoe-border-main dark:border-eoe-dark-border-main bg-eoe-bg dark:bg-eoe-dark-bg hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                  >
                    <span className="border rounded-full px-1 mr-1 border-eoe-text dark:border-eoe-dark-text">{item.id}</span> {item.statement}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DetailPanel>
      )}
    </div>
  );
};

export default AlignmentSummaryDisplay;